import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import type { Dayjs } from 'dayjs';
import apiClient from '../../../utils/axiosClient';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import type { SettlementStatusRow } from '../../../types/luckyLotterySettlementStatus.types';

type Props = {
  data: SettlementStatusRow[];
  startDate: Dayjs;
  endDate: Dayjs;
  selectedApp: string | null;
  selectedOs: string;
};

const fmtWon = (v: number) => `₩${Math.round(v ?? 0).toLocaleString('ko-KR')}`;
const fmtNum = (v: number) => Math.round(v ?? 0).toLocaleString('ko-KR');

const DATE_KEYS = new Set<keyof SettlementStatusRow>(['weekStartDate', 'weekEndDate']);
const COUNT_KEYS = new Set<keyof SettlementStatusRow>(['dau', 'ticketSum', 'ticketTxnCount']);
const fmt = (key: keyof SettlementStatusRow, v: number) =>
  COUNT_KEYS.has(key) ? fmtNum(v) : fmtWon(v);

const COLUMNS: { key: keyof SettlementStatusRow; label: string }[] = [
  { key: 'date', label: '날짜' },
  { key: 'weekStartDate', label: '회차 시작일' },
  { key: 'weekEndDate', label: '회차 종료일' },
  { key: 'dau', label: 'DAU' },
  { key: 'totalRevenue', label: '총 매출' },
  { key: 'expectedRevenue', label: '예상 수익' },
  { key: 'commission', label: '수수료' },
  { key: 'ticketSum', label: '응모권 발급 수량' },
  { key: 'ticketTxnCount', label: '응모권 발급 건수' },
  { key: 'expense', label: '비용' },
  { key: 'expenseWinning', label: '당첨 비용' },
  { key: 'expenseNpayExchange', label: 'N페이 전환 비용' },
  { key: 'expenseCurrencyExchange', label: '응모권 교환 비용' },
  { key: 'revenueSSPLottery', label: 'SSP 복권' },
  { key: 'revenueBenefitCPC', label: '베네핏 CPC' },
  { key: 'revenueRcLottery', label: 'RC 복권' },
  { key: 'revenueNaverDirect', label: '네이버 직연동' },
  { key: 'revenueCoupangSeller', label: '쿠팡 셀러' },
];

const ROWS_PER_PAGE = 31;

const cellSx = {
  fontSize: '0.8rem' as const,
  whiteSpace: 'nowrap' as const,
  py: 0.5,
  px: 1,
  border: '1px solid #f1f5f9',
};

const headSx = {
  ...cellSx,
  fontWeight: 600,
  py: 0.75,
  border: '1px solid #e2e8f0',
  backgroundColor: '#f1f5f9',
};

export default function LuckyLotterySettlementStatusTable({
  data,
  startDate,
  endDate,
  selectedApp,
  selectedOs,
}: Props) {
  const [page, setPage] = useState(0);
  const { sorted, sortDirection, toggleSort } = useDateSort(data);

  const totals = useMemo(() => {
    const result: Record<string, number> = {};
    for (const col of COLUMNS.slice(1)) {
      if (DATE_KEYS.has(col.key)) continue;
      result[col.key] = sorted.reduce((sum, row) => sum + ((row[col.key] as number) ?? 0), 0);
    }
    return result;
  }, [sorted]);

  const paged = useMemo(
    () => sorted.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [sorted, page],
  );

  const handleDownloadXlsx = useCallback(() => {
    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
    };
    if (selectedApp && selectedApp !== '앱 전체') params.app_name = selectedApp;
    if (selectedOs && selectedOs !== 'all') params.os_type = selectedOs;

    apiClient
      .get('/api/v1/stat/lucky-lottery/settlement/status/xlsx', { params, responseType: 'blob' })
      .then((res) => {
        const disposition = res.headers['content-disposition'] ?? '';
        const rfcMatch = disposition.match(/filename\*=UTF-8''([^;\n]+)/i);
        const plainMatch = disposition.match(/filename="?([^";\n]+)"?/i);
        const raw = rfcMatch?.[1] ?? plainMatch?.[1] ?? null;
        const filename = raw
          ? decodeURIComponent(raw.trim())
          : `통합지표_럭키복권_${startDate.format('YYMMDD')}_${endDate.format('YYMMDD')}.xlsx`;
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      });
  }, [startDate, endDate, selectedApp, selectedOs]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu onDownloadExcel={handleDownloadXlsx} />
      </Box>

      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, minWidth: 82 }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              {COLUMNS.slice(1).map((col) => (
                <TableCell
                  key={col.key}
                  align={DATE_KEYS.has(col.key) ? 'center' : 'right'}
                  sx={{ ...headSx, minWidth: 96 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow sx={{ backgroundColor: '#f0f4ff' }}>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>합계</TableCell>
              {COLUMNS.slice(1).map((col) => (
                <TableCell
                  key={col.key}
                  align={DATE_KEYS.has(col.key) ? 'center' : 'right'}
                  sx={{ ...cellSx, fontWeight: 700 }}
                >
                  {DATE_KEYS.has(col.key) ? '-' : fmt(col.key, totals[col.key] ?? 0)}
                </TableCell>
              ))}
            </TableRow>

            {paged.map((row) => (
              <TableRow key={row.date} hover>
                <TableCell sx={cellSx}>{row.date}</TableCell>
                {COLUMNS.slice(1).map((col) => (
                  <TableCell
                    key={col.key}
                    align={DATE_KEYS.has(col.key) ? 'center' : 'right'}
                    sx={cellSx}
                  >
                    {DATE_KEYS.has(col.key)
                      ? String(row[col.key] ?? '')
                      : fmt(col.key, row[col.key] as number)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
      />
    </Box>
  );
}
